import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CircularProgress from "@mui/joy/CircularProgress";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import React from "react";
import { Link } from "react-router-dom";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import { PutDocumentRequest } from "../../../models/document";
import { Note } from "../../../models/note";
import { PersonId } from "../../../models/person";
import { TaskId, TaskStatus } from "../../../models/task";
import {
  DocumentQuartzClientContext,
  TaskQuartzClientContext,
} from "../../../shared/services/api/contexts";
import FileUploader from "./FileUploader";

interface TableFile {
  fileName: string;
  notes: Note[];
  message: TaskStatus;
  taskId?: TaskId;
}

const getStatus = (file: TableFile): TaskStatus => {
  if (file.message) return file.message;
  return "requested";
};

const taskStatusToTitle = (status: TaskStatus) => {
  switch (status) {
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "in-progress":
      return "In Progress";
    default:
      return "Requested";
  }
};

const UploadedFilesTable = ({ tableFiles }: { tableFiles: TableFile[] }) => {
  return (
    <Table
      sx={{
        td: { verticalAlign: "middle" },
        borderRadius: "sm",
        "& th:first-of-type": { borderTopLeftRadius: "6px !important" },
        "& th:last-of-type": { borderTopRightRadius: "6px !important" },
      }}
      variant="outlined"
    >
      <thead style={{ borderRadius: "8px" }}>
        <tr>
          <th style={{ width: "50px" }}></th>
          <th>Filename</th>
          <th>Status</th>
          <th>Related note(s)</th>
        </tr>
      </thead>
      <tbody>
        {tableFiles.map((file, key) => {
          return (
            <React.Fragment key={`${file.fileName}-${file.taskId || key}`}>
              <tr>
                <td style={{ borderBottom: 0, paddingTop: "12px" }}>
                  {(() => {
                    const status = getStatus(file);
                    switch (status) {
                      case "in-progress":
                        return <CircularProgress size="sm" />;
                      case "completed":
                        return <CheckIcon />;
                      case "failed":
                        return <ErrorOutlineIcon />;
                      default:
                        return <CircularProgress size="sm" />;
                    }
                  })()}
                </td>
                <td style={{ borderBottom: 0 }}>{file.fileName}</td>
                <td style={{ borderBottom: 0 }}>{taskStatusToTitle(getStatus(file))}</td>
                <td style={{ borderBottom: 0 }}>
                  {file.notes.map(
                    (note, i) =>
                      note.note_id && (
                        <Link
                          to={`/note/${note.note_id}`}
                          target="_blank"
                          key={`${note.note_id}-${i}`}
                        >
                          <button
                            type="button"
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              font: "inherit",
                              cursor: "pointer",
                              outline: "inherit",
                              textDecoration: "underline",
                              color: "inherit",
                            }}
                            onClick={() => {
                              const id = document.getElementById(i.toString());

                              if (id) {
                                id.className = "has-text-dark";
                              }
                            }}
                            id={i.toString()}
                            className=""
                          >
                            Open
                          </button>
                        </Link>
                      )
                  )}
                </td>
              </tr>
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
};

interface UploadDocumentProps {
  readonly personId?: PersonId | null;
}

export function UploadDocument({ personId }: UploadDocumentProps) {
  // Contexts
  const { showSnackbar } = useSnackbar();
  const documentQuartzClient = React.useContext(DocumentQuartzClientContext);
  const taskQuartzClient = React.useContext(TaskQuartzClientContext);

  // Constants
  const fileTypes = ["PDF", "TXT", "DOCX", "DOC"];
  const fileMaxSize = 6;

  // States
  const [loading, setLoading] = React.useState(false);
  const [tableFiles, setTableFiles] = React.useState<TableFile[]>([]);

  const remove_prefix = (string: string) => {
    return string.split(";base64,")[1];
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(remove_prefix(fileReader.result as string));
      };
      fileReader.onerror = reject;
    });
  };

  const pollTaskStatus = async (taskId: TaskId, fileIndex: number) => {
    let taskCompleted = false;

    while (!taskCompleted) {
      try {
        const task = await taskQuartzClient.getTaskById(taskId);
        if (task.status === "completed" || task.status === "failed") {
          setTableFiles((prevTableFiles) => {
            const updatedFiles = [...prevTableFiles];
            if (fileIndex >= 0 && fileIndex < updatedFiles.length) {
              updatedFiles[fileIndex] = {
                ...updatedFiles[fileIndex],
                message: task.status,
                notes: task.output ? task.output : [],
              };
            }
            return updatedFiles;
          });
          taskCompleted = true;
        }
      } catch (e: any) {
        showSnackbar({ text: e.message, color: "danger" });
        taskCompleted = true;
      }
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Poll every second
    }
  };

  const updateTableFileWithTaskId = (fileName: string, taskId: TaskId) => {
    setTableFiles((prevTableFiles) => {
      const updatedFiles = [...prevTableFiles];
      const fileIndex = updatedFiles.findIndex((f) => f.fileName === fileName);

      if (fileIndex !== -1) {
        updatedFiles[fileIndex] = {
          ...updatedFiles[fileIndex],
          taskId: taskId,
        };
      }

      return updatedFiles;
    });
  };

  const updateTableFileWithError = (fileName: string) => {
    setTableFiles((prevTableFiles) => {
      const updatedFiles = [...prevTableFiles];
      const fileIndex = updatedFiles.findIndex((f) => f.fileName === fileName);

      if (fileIndex !== -1) {
        updatedFiles[fileIndex] = {
          ...updatedFiles[fileIndex],
          message: "failed",
        };
      }

      return updatedFiles;
    });
  };

  const processFile = async (file: File, fileArray: File[]) => {
    const tableFile = {
      fileName: file.name,
      notes: [],
      message: "in-progress",
    } as TableFile;

    setTableFiles((prevTableFiles) => [...prevTableFiles, tableFile]);

    try {
      const putDocumentRequest = {
        content: await fileToBase64(file),
        filename: file.name,
        content_type: file.type,
        person_id: personId,
      } as PutDocumentRequest;

      const task = await documentQuartzClient.postDocument(putDocumentRequest);
      updateTableFileWithTaskId(file.name, task.task_id);
      pollTaskStatus(task.task_id, fileArray.indexOf(file));
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
      updateTableFileWithError(file.name);
    }
  };

  const handleChange = async (files: File | Array<File>) => {
    setLoading(true);
    let fileArray: File[];
    if (Array.isArray(files)) {
      fileArray = files;
    } else if (files instanceof FileList) {
      fileArray = Array.from(files);
    } else {
      fileArray = [files];
    }

    await Promise.all(fileArray.map((file) => processFile(file, fileArray)));
    setLoading(false);
  };

  return (
    <Stack spacing={2} direction="column">
      <FileUploader
        handleChange={handleChange}
        multiple={true}
        name="file"
        types={fileTypes}
        maxSize={fileMaxSize}
        disabled={loading}
      />
      {tableFiles && tableFiles.length > 0 && <UploadedFilesTable tableFiles={tableFiles} />}
    </Stack>
  );
}
